import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, Send, Loader2 } from "lucide-react";
import { BRAND } from "@/lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Obra Nueva y Edificación",
  "Reformas Integrales",
  "Rehabilitación de Edificios",
  "Albañilería General",
  "Solados y Alicatados",
  "Todo Tipo de Fachadas",
  "Otro / Consulta general",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: SERVICE_OPTIONS[0],
    location: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Por favor, completa todos los campos obligatorios.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quote-requests`, form);
      toast.success("¡Solicitud enviada! Te contactaremos en menos de 24h laborables.");
      setForm({
        name: "",
        email: "",
        phone: "",
        service: SERVICE_OPTIONS[0],
        location: "",
        message: "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (Array.isArray(err?.response?.data) ? err.response.data[0]?.msg : null) ||
        "No se pudo enviar la solicitud. Inténtalo de nuevo o llámanos.";
      toast.error(typeof detail === "string" ? detail : "Error al enviar la solicitud.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contacto" data-testid="contact-section" className="relative py-24 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F26522]/10 text-[#D95212] text-xs font-mono-accent uppercase tracking-[0.18em] mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#F26522]" />
            Contacto
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0B1528] tracking-tight leading-[1.05]">
            Solicita tu{" "}
            <span className="text-[#F26522]">presupuesto gratis</span> sin compromiso.
          </h2>
          <p className="mt-4 text-slate-600 text-base sm:text-lg">
            Cuéntanos tu proyecto y te responderemos en menos de{" "}
            <strong className="text-[#0B1528]">24 horas laborables</strong>.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Form */}
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="lg:col-span-7 bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-xl shadow-slate-900/5"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Nombre completo *">
                <input
                  data-testid="contact-input-name"
                  type="text"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Ej. Juan Pérez"
                  required
                  className="input-amlou"
                />
              </Field>
              <Field label="Teléfono *">
                <input
                  data-testid="contact-input-phone"
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="Ej. 600 123 456"
                  required
                  className="input-amlou"
                />
              </Field>
              <Field label="Email *">
                <input
                  data-testid="contact-input-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="tu@correo.com"
                  required
                  className="input-amlou"
                />
              </Field>
              <Field label="Localidad">
                <input
                  data-testid="contact-input-location"
                  type="text"
                  value={form.location}
                  onChange={update("location")}
                  placeholder="Ej. Cunit"
                  className="input-amlou"
                />
              </Field>
              <Field label="Tipo de servicio *" full>
                <select
                  data-testid="contact-input-service"
                  value={form.service}
                  onChange={update("service")}
                  className="input-amlou"
                >
                  {SERVICE_OPTIONS.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </Field>
              <Field label="Describe tu proyecto *" full>
                <textarea
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={update("message")}
                  rows={5}
                  placeholder="Cuéntanos qué necesitas, superficie aproximada, plazos, etc."
                  required
                  className="input-amlou resize-none"
                />
              </Field>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
              <p className="text-xs text-slate-500 font-mono-accent">
                * Campos obligatorios · Tus datos están protegidos
              </p>
              <button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#F26522] hover:bg-[#D95212] text-white font-semibold text-sm shadow-lg shadow-orange-900/25 transition-all hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Enviando...
                  </>
                ) : (
                  <>
                    Enviar Solicitud <Send className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Info sidebar */}
          <aside className="lg:col-span-5 space-y-4">
            <InfoCard
              testId="contact-card-phone"
              Icon={Phone}
              title="Llámanos directamente"
              subtitle="Lun–Sáb · 8:00–20:00"
            >
              <a
                href={BRAND.phonePrimaryHref}
                className="block font-display text-xl font-bold text-[#0B1528] hover:text-[#F26522] transition-colors"
              >
                {BRAND.phonePrimary}
              </a>
              <a
                href={BRAND.phoneSecondaryHref}
                className="block font-display text-lg font-semibold text-slate-600 hover:text-[#F26522] transition-colors"
              >
                {BRAND.phoneSecondary}
              </a>
            </InfoCard>

            <InfoCard
              testId="contact-card-email"
              Icon={Mail}
              title="Correo electrónico"
              subtitle="Respuesta en 24h laborables"
            >
              <a
                href={BRAND.emailHref}
                className="block font-mono-accent text-sm text-[#0B1528] hover:text-[#F26522] transition-colors break-all"
              >
                {BRAND.email}
              </a>
            </InfoCard>

            <InfoCard
              testId="contact-card-location"
              Icon={MapPin}
              title="Ubicación"
              subtitle="Servicio en toda la provincia"
            >
              <div className="font-display font-semibold text-[#0B1528]">
                Cunit (Tarragona)
              </div>
              <div className="text-sm text-slate-500 mt-0.5">Baix Penedès · Cataluña</div>
            </InfoCard>

            <a
              data-testid="contact-whatsapp-cta"
              href={BRAND.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-4 p-5 rounded-2xl bg-[#25D366] hover:bg-[#20BA57] text-white transition-all hover:-translate-y-0.5 shadow-lg shadow-emerald-900/20"
            >
              <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                <MessageCircle className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="font-display font-bold text-lg leading-tight">
                  Contactar por WhatsApp
                </div>
                <div className="text-sm text-white/85">Respuesta rápida al momento</div>
              </div>
            </a>
          </aside>
        </div>
      </div>

      <style>{`
        .input-amlou {
          display: block;
          width: 100%;
          padding: 12px 14px;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          background: #f8fafc;
          font-size: 14px;
          color: #0f172a;
          transition: border-color .2s, background .2s, box-shadow .2s;
          font-family: inherit;
        }
        .input-amlou::placeholder { color: #94a3b8; }
        .input-amlou:focus {
          outline: none;
          background: #fff;
          border-color: #F26522;
          box-shadow: 0 0 0 3px rgba(242, 101, 34, 0.15);
        }
      `}</style>
    </section>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={"block " + (full ? "sm:col-span-2" : "")}>
      <span className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider font-mono-accent">
        {label}
      </span>
      {children}
    </label>
  );
}

function InfoCard({ Icon, title, subtitle, children, testId }) {
  return (
    <div data-testid={testId} className="bg-[#F8FAFC] rounded-2xl p-6 border border-slate-100">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-[#0B1528] text-white flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-[#0B1528]">{title}</div>
          <div className="text-xs text-slate-500 mb-2.5 font-mono-accent">{subtitle}</div>
          {children}
        </div>
      </div>
    </div>
  );
}
